import React, { useMemo } from "react";
import type { SectionBlogCarouselProps } from "../../content/section-blog-carousel";
import { useContent, useLocalizedUrl } from "../../hooks";
import { Button, Carousel, ResponsiveImage, SectionHeader } from "../ui";

export type BlogCarouselSectionProps = SectionBlogCarouselProps & {
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion? (12.8.2026)
     *
     * Im Kapitel «Wer wir sind» folgt dieser Teil auf den Absatz ueber das
     * Haus. Die Kapitelueberschrift steht als `h2`, «Was wir sonst noch
     * machen» faellt auf `h3` und die Beitragstitel auf `h4`.
     */
    nested?: boolean;
};

/**
 * «Was wir sonst noch machen» auf der Startseite (v0.3 F5).
 *
 * Artikel kommen aus der `blog`-Collection (sprachgefiltert von
 * `LanguageContext`), nicht aus dem MDX.
 *
 * Der Ziel-Link folgt derselben Regel wie `BlogCard`: Beitraege der Kategorie
 * «success-stories» fuehren ein `successStorySlug` und liegen unter
 * /success-stories/<slug>, nicht unter ihrem Blog-Pfad. Ohne diese Regel
 * zeigten 4 von 12 Karten ins Leere — im dist nachgezaehlt: mit der Regel
 * loesen 12/12 auf, ohne sie 8/12.
 */
export const BlogCarouselSection: React.FC<BlogCarouselSectionProps> = ({
    badge,
    title,
    description,
    limit,
    cta,
    ariaLabel,
    nested = false,
}) => {
    const { blog } = useContent();
    const href = useLocalizedUrl(cta?.href ?? "/blog");

    const items = useMemo(() => {
        const all = Object.values(blog ?? {}).sort((a, b) =>
            String(b.publishedAt ?? "").localeCompare(String(a.publishedAt ?? ""))
        );
        return typeof limit === "number" ? all.slice(0, limit) : all;
    }, [blog, limit]);

    if (items.length === 0) {
        return null;
    }

    return (
        <section data-testid="blog-carousel-section" className={nested ? "" : "bg-white py-24"}>
            <div className="container mx-auto px-6 md:px-12 lg:px-16">
                <SectionHeader
                    badge={nested ? undefined : badge}
                    title={title}
                    subtitle={description}
                    className="mb-12"
                    headingLevel={nested ? 3 : 2}
                />

                <Carousel
                    items={items}
                    ariaLabel={ariaLabel ?? title}
                    renderItem={item => (
                        <BlogCard
                            slug={item.slug}
                            successStorySlug={item.successStorySlug}
                            title={item.title}
                            excerpt={item.excerpt}
                            featuredImage={item.featuredImage}
                            featuredImageAlt={item.featuredImageAlt}
                            nested={nested}
                        />
                    )}
                />

                {cta?.text && (
                    <div className="mt-12 text-center">
                        <Button href={href} variant="secondary" size="md">
                            {cta.text}
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
};

/**
 * Eigene Komponente, weil `useLocalizedUrl` ein Hook ist: in `renderItem`
 * aufgerufen liefe er in einer Callback-Funktion und damit ausserhalb der
 * Rules of Hooks.
 *
 * Die ganze Karte ist der Link — nicht ein «Weiterlesen» darunter. Das spart
 * eine fest verdrahtete Beschriftung (Regel 1: kein Text im Bauteil) und gibt
 * dem Screenreader den Titel als Linktext statt zwoelfmal dasselbe Wort.
 */
const BlogCard: React.FC<{
    slug: string;
    successStorySlug?: string;
    title: string;
    excerpt?: string;
    featuredImage?: string;
    featuredImageAlt?: string;
    nested?: boolean;
}> = ({ slug, successStorySlug, title, excerpt, featuredImage, featuredImageAlt, nested = false }) => {
    const target = useLocalizedUrl(successStorySlug ? `/success-stories/${successStorySlug}` : slug);
    const CardHeading = nested ? "h4" : "h3";

    return (
        <a
            href={target}
            className="mx-3 flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-lg transition-shadow hover:shadow-xl"
        >
            {featuredImage && (
                <ResponsiveImage
                    src={featuredImage}
                    alt={featuredImageAlt ?? title}
                    className="h-44 w-full"
                    widths={[320, 640]}
                    sizes="(max-width: 768px) 100vw, 33vw"
                    objectFit="cover"
                />
            )}
            <div className="flex flex-grow flex-col p-6">
                <CardHeading className="mb-2 text-lg font-black text-primary">{title}</CardHeading>
                <p className="line-clamp-3 flex-grow leading-relaxed text-black">{excerpt}</p>
            </div>
        </a>
    );
};

export default BlogCarouselSection;
